<script setup lang="ts">
    import { ref, onMounted, watch } from 'vue';
    import { http } from '@/shared/httpWrapper.js';
    import { useRoute, useRouter } from 'vue-router';
    import { getSchemaDTO } from '@/shared/helpers/api';
    import useShow from '@/shared/composables/useShow.js';
    import _ from 'lodash';
    import SchemaButtons from './components/SchemaButtons.vue';
    import SchemaModel from './views/SchemaModel.vue';
    import SchemaClasses from './views/SchemaClasses.vue';
    import { SchemaClass, SchemaDTO } from '@/shared/helpers/api/types/schemaModel.ts';
    import { shortDate } from '@/formatters';

    type SchemaTabType = 'Info' | 'Graph' | 'Issues' | 'Classes';

    const props = defineProps({
        identity: {
            type: String,
            required: true
        },
        classIdentity: {
            type: String,
            required: false,
            default: null
        }
    });

    const { showError } = useShow();
    const route = useRoute();
    const router = useRouter();

    const schema = ref<SchemaDTO | null>(null);
    const activeTab = ref<SchemaTabType>('Info');
    const error = ref<any | null>(null);
    const selectedClass = ref<SchemaClass | undefined>();

    function updateRoute(tabPage: string): void {
        if (tabPage.toLowerCase() === 'classes' && selectedClass.value) {
            router.push(`/Schema/${props.identity}/${tabPage}/${selectedClass.value.name}`);
        } else {
            router.push(`/Schema/${props.identity}/${tabPage}`);
        }
    }
    function setActiveTab(tabPage: string): void {
        const tab = tabPage.toLowerCase();

        if (tab === 'info') {
            activeTab.value = 'Info';
        } else if (tab === 'graph') {
            activeTab.value = 'Graph';
        } else if (tab === 'issues') {
            activeTab.value = 'Issues';
        } else if (tab === 'classes') {
            activeTab.value = 'Classes';
        } else {
            activeTab.value = 'Info';
        }
    }

    watch(selectedClass, (newVal, oldVal) => {
        if (newVal !== oldVal && newVal) {
            updateRoute(activeTab.value);
        }
    });

    watch(activeTab, (newVal, oldVal) => {
        if (newVal !== oldVal && newVal) {
            updateRoute(newVal);
        }
    });

    onMounted(async () => {
        try {
            const schemaOptions = {
                schemaName: props.identity
            };
            const response = await getSchemaDTO(http, schemaOptions);
            schema.value = response.data;
            schema.value.classes = _.sortBy(schema.value.classes, (x) => x.name);

            if (props.classIdentity) {
                selectedClass.value = schema.value.classes.find(c => c.name.toLowerCase() === props.classIdentity.toLowerCase());
            }
        } catch (err) {
            error.value = err;
            showError(err);
        }
        const tab = route.path.split('/').filter(part => part !== '')[2];
        setActiveTab(tab);
    });
</script>

<template>
    <div>
        <b-field>
            <router-link to="/Schema">
                &laquo; Back to list
            </router-link>
        </b-field>
        <div
            v-if="error">
            <h1 class="title is-2">
                Something went wrong when composing schema {{ identity }}!!!
            </h1>
            <div class="columns">
                <div class="column is-6">
                    <div
                        class="notification is-danger">
                        <p>{{ error }}</p>
                    </div>
                </div>
                <div class="column is-6">
                    <schema-buttons :identity="identity" />
                </div>
            </div>
        </div>
        <div
            v-else-if="schema">
            <h1 class="title">
                Schema: {{ schema.name }}
            </h1>
            <h2 class="subtitle">
                {{ schema.description }}
            </h2>

            <b-tabs
                v-model="activeTab"
                type="is-boxed">
                <b-tab-item
                    value="Info"
                    label="Info"
                    icon="information">
                    <div class="columns">
                        <div class="column is-half">
                            <h4 class="title is-4 block">
                                Comments
                            </h4>
                            <p class="block">
                                {{ schema.comments }}
                            </p>
                            <h4 class="title is-4 block">
                                Versions
                            </h4>
                            <b-table
                                :data="schema.versions"
                                :striped="true"
                                :narrowed="true"
                                :hoverable="false"
                                class="classTable">
                                <b-table-column
                                    v-slot="slotProps"
                                    field="versionNumber"
                                    label="Version number">
                                    {{ slotProps.row.versionNumber }}
                                </b-table-column>
                                <b-table-column
                                    v-slot="slotProps"
                                    field="description"
                                    label="Description">
                                    {{ slotProps.row.description }}
                                </b-table-column>
                                <b-table-column
                                    v-slot="slotProps"
                                    field="releaseDate"
                                    label="Release date">
                                    {{ shortDate(slotProps.row.releaseDate) }}
                                </b-table-column>
                            </b-table>
                            <h4 class="title is-4 block">
                                References
                            </h4>
                            <p class="block">
                                {{ schema.references }}
                            </p>
                        </div>
                        <div
                            class="column">
                            <schema-buttons :identity="identity" />
                        </div>
                    </div>
                </b-tab-item>
                <b-tab-item
                    value="Classes"
                    label="Classes"
                    icon="format-list-bulleted">
                    <schema-classes
                        v-model="selectedClass"
                        :schema="schema" />
                </b-tab-item>
                <b-tab-item
                    value="Graph"
                    label="Graph"
                    icon="graph">
                    <schema-model :identity="identity" />
                </b-tab-item>
                <b-tab-item
                    value="Issues">
                    <template #header>
                        <b-icon icon="alert" />
                        <span> Issues <b-tag
                            v-show="schema.warnings.length > 0"
                            type="is-danger"
                            rounded> {{ schema.warnings.length }} </b-tag> </span>
                    </template>
                    <div
                        v-for="(w, i) in schema.warnings"
                        :key="i"
                        class="notification is-warning">
                        {{ w.description }}
                    </div>
                    <p v-show="schema.warnings.length === 0">
                        There are no issues.
                    </p>
                </b-tab-item>
            </b-tabs>
        </div>
    </div>
</template>

<style scoped>
p {
    margin-bottom:10px;
}
h2 {
    font-weight: bold;
}
h3 {
    font-weight: bold;
}
.classTable {
    margin-bottom: 25px;
    overflow-x: hidden;
}
</style>
